import React from "react";
import { Grid, Backdrop, Typography, Button, Paper, Box, styled, useTheme, useMediaQuery } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useSpring, animated, config } from "react-spring"; // Updated to use hooks API
import { useNavigate } from "react-router-dom";

import { ReactComponent as HelloIcon } from "./Icons/hello.svg";
import { ReactComponent as SkillsIcon } from "../Common/Icons/skill.svg";
import { ReactComponent as InterestsIcon } from "../Common/Icons/interests.svg";
import { ReactComponent as StylesIcon } from "../Common/Icons/styles.svg";
import { ReactComponent as SmallCoin } from "../Common/Icons/smallCoin.svg";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { clickHello } from "../../actions/user_actions";

const StyledIconDiv = styled(animated.div)(({ theme }) => ({
  width: 162,
  height: 166,
  margin: 'auto'
}));

const Hello = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const { firstName = "", showHello = false, firstLogin, internalMobilityProvider, orgDetails = {} } = useSelector(state => state.user);
  const { coins } = useSelector(state => state.rewards);

  const { name = "" } = orgDetails;
  
  // Modern react-spring hook to handle the animation
  const animatedProps = useSpring({
    from: { transform: "scale(0.5)" },
    to: { transform: "scale(1)" },
    delay: 100,
    config: config.wobbly,
  });

  return (
    <Backdrop
      open={showHello}
      transitionDuration={{
        appear: 0,
        exit: 500,
        enter: 0,
      }}
      sx={{ zIndex: 8000 }}
    >
      <Box sx={{
        flexGrow: 1,
        zIndex: 12000,
        position: "absolute",
        backgroundColor: "#F8F9FA",
        width: "100%",
        height: "100%",
        overflow: 'scroll'
      }}>
        <Grid
          container
          spacing={0}
          direction="column"
          justifyContent="center"
          alignItems="center"
          sx={{ height: "100%", mt: 12.5 }}
        >
          <Grid item sx={{ textAlign: "center", width: "80%" }}>
            <StyledIconDiv style={animatedProps}>
              <HelloIcon sx={{ width: 162, height: 166 }} />
            </StyledIconDiv>

            <Typography variant="h4" sx={{
              fontSize: 28,
              fontWeight: 600,
              color: "#202A3E",
              mt: 2,
              mb: 2,
            }}>
              {firstLogin ? `Hello ${firstName}` : `Welcome back ${firstName}`}
            </Typography>
            {firstLogin ? (
              internalMobilityProvider ? (
                <Typography variant="body1" sx={{
                  mx: { xs: 2, md: 8 },
                  textAlign: "center",
                  fontSize: { xs: 14, sm: 18 },
                  color: "#5A6375",
                  mb: 2,
                }}>
                  Welcome aboard! Thanks for signing up, it's fantastic to have you here!
                  <br />
                  Engage with our virtual assistant, and over the next few minutes, we'll explore what sets you apart.
                  <br />
                  {`This exploration will enable us to identify the perfect opportunities for you, granting ${name} insight into your skills, knowledge, abilities, competencies, and potential.`}
                  <br />
                </Typography>
              ) : (
                <Typography variant="body1" sx={{
                  mx: { xs: 2, md: 8 },
                  textAlign: "center",
                  fontSize: { xs: 14, sm: 18 },
                  color: "#5A6375",
                  mb: 2,
                }}>
                  You’ve taken a step towards a better career. Now it’s our turn to do our bit.
                  <br />
                  We’ll unearth your potential in just four minutes by understanding what makes you tick. We’ll match you to jobs that you never knew you could do.
                  <br />
                  Just chat to our virtual assistant and let us do the job searching for you. It’s simple, and you’ll learn a lot about yourself along the way – including your skills, interests and styles.
                  <br />
                  Once you’ve answered our questions - we remember all the details so you won’t need to do it again
                </Typography>
              )
            ) : (
              internalMobilityProvider ? (
                <Typography variant="body1" sx={{
                  mx: { xs: 2, md: 8 },
                  textAlign: "center",
                  fontSize: { xs: 14, sm: 18 },
                  color: "#5A6375",
                  mb: 2,
                }}>
                  Ensure your profile remains current to ensure accurate matching with the right opportunities.
                  <br />
                  {`Keeping your information up to date also allows ${name} to stay informed about your progress and development.`}
                  <br />
                  We're thrilled to have you back!
                </Typography>
              ) : (
                <Typography variant="body1" sx={{
                  mx: { xs: 2, md: 8 },
                  textAlign: "center",
                  fontSize: { xs: 14, sm: 18 },
                  color: "#5A6375",
                  mb: 2,
                }}>
                  Whether you want to find jobs based on experience or find something new based on your skills, styles or interests.
                  <br />
                  We’ve been mining the internet to find you the best matched jobs.
                  <br />
                </Typography>
              )
            )}
            <Box sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <Box sx={{ maxWidth: 880, mx: "auto", mb: 2, pl: 2, pr: 2 }}>
                <Grid container spacing={2} direction="row" justifyContent="center" alignItems="stretch">
                  <Grid item size={4}>
                    <Paper sx={{ height: "100%", p: 1.875, textAlign: "left" }}>
                      <Grid container spacing={1} direction="row" justifyContent="center" alignItems="center">
                        <Grid item>
                          <SkillsIcon />
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: 20, color: "#53D28E" }}
                          >
                            Skills
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="body1"
                        sx={{ fontSize: 18, color: "#5A6375", textAlign: "left", mt: 0.625 }}
                      >
                        {firstLogin ? "We’ll assess what you can do based on what you’ve done in previous jobs" : "We’ve found the best jobs for you based on your skills, knowledge and abilities"}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item size={4}>
                    <Paper sx={{ height: "100%", p: 1.875, textAlign: "left" }}>
                      <Grid container spacing={1} direction="row" justifyContent="center" alignItems="center">
                        <Grid item>
                          <InterestsIcon />
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: 20, color: "#2988FF" }}
                          >
                            Interests
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="body1"
                        sx={{ fontSize: 18, color: "#5A6375", textAlign: "left", mt: 0.625 }}
                      >
                        {firstLogin ? "We’ll analyse what you like doing – in and out of work" : "We’re bringing opportunities to you centred on your interests - we all perform better doing jobs we enjoy"}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item size={4}>
                    <Paper sx={{ height: "100%", p: 1.875, textAlign: "left" }}>
                      <Grid container spacing={1} direction="row" justifyContent="center" alignItems="center">
                        <Grid item>
                          <StylesIcon />
                        </Grid>
                        <Grid size="grow">
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, fontSize: 20, color: "#FF9800" }}
                          >
                            Styles
                          </Typography>
                        </Grid>
                      </Grid>

                      <Typography
                        variant="body1"
                        sx={{ fontSize: 18, color: "#5A6375", textAlign: "left", mt: 0.625 }}
                      >
                        {firstLogin ? "We’ll figure out your strengths and how you like to work" : "All occupations require a unique combination of resilience, brainpower and agility; we’ve found you jobs based on your unique profile"}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            {!firstLogin && coins && (
              <Typography variant="body1" sx={{
                mx: { xs: 2, md: 8 },
                textAlign: "center",
                fontSize: { xs: 14, sm: 18 },
                color: "#5A6375",
                mb: 2,
              }}>
                You have {coins}
                <SmallCoin sx={{ marginInline: 0.625, mb: -0.125 }} />
                coins
              </Typography>
            )}
            {!firstLogin && (
              <Typography variant="body1" sx={{
                mx: { xs: 2, md: 8 },
                textAlign: "center",
                fontSize: { xs: 14, sm: 18 },
                color: "#5A6375",
                mb: 2,
              }}>
                You can use your coins to buy Application Assistant content '
                <RemoveIcon sx={{ mb: -0.375, marginInline: 0.625 }} fontSize="small" />
                <SmallCoin sx={{ marginInline: 0.625, mb: -0.125 }} />
                ', and don’t worry if you run out because you can earn more '
                <AddIcon sx={{ mb: -0.375, marginInline: 0.625 }} fontSize="small" />
                <SmallCoin sx={{ marginInline: 0.625, mb: -0.125 }} />
                ' or buy more.
              </Typography>
            )}
            {!internalMobilityProvider && (
              <Typography variant="body1" sx={{
                mx: { xs: 2, md: 8 },
                textAlign: "center",
                fontSize: { xs: 14, sm: 18 },
                color: "#5A6375",
                mb: 2,
              }}>
                {firstLogin && "Let's get started.  "}
              </Typography>
            )}
            <Button
              id="lets-go-btn"
              variant="contained"
              sx={{ px: 3.75 }}
              color="primary"
              onClick={() => {
                dispatch(clickHello());
              }}
            >
              Reveal Opportunities
            </Button>
            <Box sx={{ height: 60 }} />
          </Grid>
        </Grid>
      </Box>
    </Backdrop>
  );
};

export default Hello;